import { FIELD_NAMES, QUESTION_IDS, SKIP_REASON_CODES } from 'consts';
import {
  FormValues,
  DynamicOptions,
  ErrorPriority,
  SelectFieldOption,
  School,
  BasicFormWizardContext,
} from 'types';
import { LogInfo } from 'utils/logging';
import { LogError } from 'utils/logging/LogError';
import { getFieldValue } from 'utils/formValuesUtils';

function getShouldSkip(formValues: FormValues, name: FIELD_NAMES): boolean {
  if (
    formValues[name] &&
    typeof formValues[name] === 'object' &&
    'shouldSkip' in formValues[name]
  ) {
    return formValues[name].shouldSkip ?? false;
  }
  return false;
}

function getSkipReason(
  formValues: FormValues,
  name: FIELD_NAMES
): SKIP_REASON_CODES | undefined | null {
  if (
    formValues[name] &&
    typeof formValues[name] === 'object' &&
    'skipReason' in formValues[name]
  ) {
    return formValues[name].skipReason;
  }
  return undefined;
}

export function setValueIfOnlyOneOption(
  formValues: FormValues,
  dynamicOptions: DynamicOptions,
  allQuestionsInForm: BasicFormWizardContext['allQuestionsInForm']
): FormValues {
  if (!allQuestionsInForm) {
    return formValues;
  }

  try {
    const _formValues = { ...formValues };

    Object.values(dynamicOptions).forEach((question) => {
      if (!question.options) {
        return;
      }

      const { name } = allQuestionsInForm[question.questionId];
      const isASkippedQuestion = getShouldSkip(_formValues, name);
      const skippedReason = getSkipReason(_formValues, name);
      const isTaxonomySkippedConcentrationsQuestion =
        isASkippedQuestion &&
        skippedReason === SKIP_REASON_CODES.TAXONOMY &&
        question.questionId === QUESTION_IDS.CONCENTRATIONS;

      // If this question was skipped due to user interaction do nothing
      if (
        isASkippedQuestion &&
        skippedReason !== SKIP_REASON_CODES.SINGLE_VALUE &&
        skippedReason !== SKIP_REASON_CODES.CURATED_QUESTION_SYNC &&
        !isTaxonomySkippedConcentrationsQuestion
      ) {
        if (!skippedReason) {
          LogError('A skipped question does not have a reason code.', {
            priority: ErrorPriority.ONE,
            description: `fieldName: ${name} questionId: ${question.questionId}`,
          });
        }
        return;
      }

      // note that its possible current value is an array
      const currentValue = getFieldValue(_formValues, name);

      // handle grouped options as well
      let currentOptions = question.options
        .reduce((acc, group) => {
          if (typeof group === 'object' && 'options' in group) {
            acc.push(group.options);
          } else {
            acc.push(group);
          }
          return acc;
        }, [] as (SelectFieldOption | School | SelectFieldOption[])[])
        .flat();

      const isObsoleteOption = !currentOptions.find((option) => {
        if (Array.isArray(currentValue)) {
          return currentValue.find((selection) => {
            if ('guid' in option && selection === option.guid) {
              return true;
            }

            if ('value' in option && selection === option.value) {
              return true;
            }

            return false;
          });
        }

        if ('guid' in option && currentValue === option.guid) {
          return true;
        }

        if ('value' in option && currentValue === option.value) {
          return true;
        }

        return false;
      });

      // if current question is Concentrations strip out the UNDECIDED option
      // we only care about actual concentrations when we decide to preselect
      if (question.questionId === QUESTION_IDS.CONCENTRATIONS) {
        currentOptions = currentOptions.filter((option) => {
          if (typeof option === 'object' && 'guid' in option) {
            return option.guid !== '00000000-0000-0000-0000-000000000000';
          }
          return true;
        });
      }

      if (
        currentOptions.length === 1 &&
        question.questionId !== QUESTION_IDS.MICRO_PORTAL_DYNAMIC_TCPA &&
        question.questionId !==
          QUESTION_IDS.MICRO_PORTAL_PRIMARY_SCHOOLS_SELECTION
      ) {
        LogInfo(
          `setValueIfOnlyOneOption: Setting single value for question: ${name}`,
          {
            description: `fieldName: ${name}  questionId: ${question.questionId}`,
          }
        );
        _formValues[name] = {
          ...(currentOptions[0] as SelectFieldOption),
          shouldSkip: true,
          skipReason: SKIP_REASON_CODES.SINGLE_VALUE,
        };
      } else if (skippedReason === SKIP_REASON_CODES.TAXONOMY) {
        // do nothing - due to recent changes concentrations can get this far, but we do not want to mess with it unless it is a single value
      } else if (isObsoleteOption || currentOptions.length === 0) {
        LogInfo(`setValueIfOnlyOneOption: Deleting question: ${name}`, {
          description: `fieldName: ${name} questionId: ${question.questionId}`,
        });
        delete _formValues[name];
      } else if (
        currentOptions.length > 1 &&
        isASkippedQuestion &&
        _formValues[name] &&
        typeof _formValues[name] === 'object' &&
        'shouldSkip' in _formValues[name]
      ) {
        _formValues[name].shouldSkip = false;
      }
    });
    return _formValues;
  } catch (error) {
    LogError(
      `setValueIfOnlyOneOption: failed due to ${
        error instanceof Error ? error.message : 'unknown error'
      }`,
      {
        priority: ErrorPriority.ONE,
      }
    );
    return formValues;
  }
}
